import { useQuery } from '@tanstack/react-query';
import {
  hasUsableAccessToken,
  isAppAuthRequiredError,
  useAppAuthState,
} from '../lib/app-auth';
import { listLinkedAccounts } from '../lib/auth-api';

const AUTH_LINKED_ACCOUNTS_QUERY_KEY = ['auth', 'linked-accounts'] as const;

export function useAuth() {
  const authState = useAppAuthState();
  const user = authState.user;
  const isAuthenticated = authState.status === 'authenticated' && !!user;
  const hasReadyAccessToken = hasUsableAccessToken(authState);
  const isTokenReady = isAuthenticated && hasReadyAccessToken;
  const isTokenRefreshing =
    isAuthenticated && !hasReadyAccessToken && authState.tokenStatus !== 'unavailable';
  const isAuthLoading =
    !authState.isBootstrapped ||
    authState.status === 'loading' ||
    isTokenRefreshing;
  const isUaePassVerified = !!user?.isUaePassVerified;
  const requiresCredentialBootstrapCheck =
    isAuthenticated && !isUaePassVerified;
  const linkedAccountsQuery = useQuery({
    queryKey: [...AUTH_LINKED_ACCOUNTS_QUERY_KEY, user?.id ?? 'guest'],
    queryFn: listLinkedAccounts,
    enabled: requiresCredentialBootstrapCheck && isTokenReady && !!user?.id,
    staleTime: 1000 * 60,
    retry: (failureCount, error) =>
      !isAppAuthRequiredError(error) && failureCount < 2,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const linkedAccounts = user?.id ? linkedAccountsQuery.data ?? [] : [];
  const hasCredentialAccount = linkedAccounts.some(
    (account) => account.providerId === 'credential',
  );
  // Only gate the app on the INITIAL check: once linked accounts are known,
  // a failed background refetch (backgrounded tab, network blip) must not
  // kick the user out to /auth-bootstrap-check.
  const bootstrapCheckFailed =
    requiresCredentialBootstrapCheck &&
    linkedAccountsQuery.isError &&
    !linkedAccountsQuery.data;
  const needsPasswordBootstrap =
    requiresCredentialBootstrapCheck &&
    linkedAccountsQuery.isSuccess &&
    !hasCredentialAccount;
  const isBootstrapLoading =
    requiresCredentialBootstrapCheck && linkedAccountsQuery.isPending;

  return {
    session: user ? { user } : null,
    user,
    linkedAccounts,
    hasCredentialAccount,
    isUaePassVerified,
    bootstrapCheckFailed,
    needsPasswordBootstrap,
    isAuthenticated,
    hasUsableAccessToken: hasReadyAccessToken,
    isTokenReady,
    isOnboardingCompleted: !!user?.isOnboardingCompleted,
    isBootstrapLoading,
    isAuthLoading,
    isLoading: isAuthLoading || isBootstrapLoading,
    retryBootstrapCheck: () => linkedAccountsQuery.refetch(),
    impersonation: authState.impersonation ?? null,
  };
}

export { AUTH_LINKED_ACCOUNTS_QUERY_KEY };
